import { Component, computed, input, model, OnChanges, output, signal, SimpleChanges } from '@angular/core'
import {
  Entity,
  EntityDetailsField,
  EntityTypeMetadata,
  getDetailsFieldLabel,
} from './entity-metadata'
import { AgGridAngular } from 'ag-grid-angular'
import { ColDef, GetRowIdFunc, GridApi, GridReadyEvent, SelectionChangedEvent, ValueFormatterFunc } from 'ag-grid-community'
import { SearchFieldComponent } from '../shared/components/search-field/search-field.component'
import { formatIsoDateOnly } from '../shared/utilities/date'
import { newEntityFieldLabels } from './new-entity.component'
import { booleanFormatter } from '../shared/utilities/formatters'

const accept = ({ entity_code, entity_name }: Entity, filterString: string): boolean =>
  entity_name.toLowerCase().includes(filterString) || entity_code.toLowerCase().includes(filterString)


const formatIsoDate: ValueFormatterFunc<Entity, string> = ({ value }) =>
  formatIsoDateOnly(value)

const createColDef = (
  field: EntityDetailsField,
  options: Partial<ColDef> = {},
): ColDef => ({
  field,
  ...options,
})

const entityColumnDefs: readonly ColDef<Entity>[] = [
  {
    field: 'entity_code',
    headerName: newEntityFieldLabels.entity_code,
  },
  {
    field: 'entity_name',
    headerName: newEntityFieldLabels.entity_name,
    width: 200,
  },
]

const effectiveDateColumnDefs: readonly ColDef<Entity>[] = [
  createColDef('effective_date', { valueFormatter: formatIsoDate }),
  createColDef('termination_date', { valueFormatter: formatIsoDate }),
]
const entitySpecificColumnDefs: readonly ColDef<Entity>[] = [
  createColDef('entity_detail_value'),
  createColDef('entity_business_type'),
  createColDef('asset_name'),
  createColDef('oasis_number'),
  createColDef('miso_id'),
  createColDef('min_limit', { cellClass: 'ag-right-aligned-cell' }),
  createColDef('max_limit', { cellClass: 'ag-right-aligned-cell' }),
  createColDef('mdma_weather_sensitive_flag', { valueFormatter: booleanFormatter, width: 210 }),
]

@Component({
  selector: 'app-entity-list',
  imports: [AgGridAngular, SearchFieldComponent],
  template: `
    <div class='filter-header mb-3'>
      <app-search-field
          placeholder='Filter by code/name'
          [(filterStringModel)]="filterStringModel"
      />
      <div class='status'>
        {{ statusMessage() }}
      </div>
    </div>
    <ag-grid-angular
        class='app-table'
        [defaultColDef]="defaultColDef"
        [columnDefs]="columnDefs()"
        [rowData]="filteredEntities()"
        [getRowId]="getRowId"
        [suppressNoRowsOverlay]="true"
        [suppressPaginationPanel]="true"
        [suppressDragLeaveHidesColumns]="true"
        [rowSelection]="{ mode: 'singleRow' }"
        (selectionChanged)='onSelectionChanged($event)'
        (gridReady)="onGridReady($event)"
    />
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      
      .filter-header {
        display: flex;
        
        app-search-field, .status {
          flex-basis: 0;
        }
        app-search-field {
          flex-grow: 2;
        }
        .status {
          flex-grow: 1;
          text-align: right;
          padding-top: 10px;
          padding-left: 10px;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }
      .app-table {
        flex-basis: 0;
        flex-grow: 1;
      }
    }
  `,
})
export class EntityListComponent implements OnChanges {
  private gridApi: GridApi | undefined

  readonly entities = input.required<readonly Entity[]>()
  readonly entityTypeMetadata = input.required<EntityTypeMetadata | undefined>()
  readonly selectedEntity = input.required<Entity | undefined>()

  readonly selectionChange = output<string | undefined>()

  private readonly selectionChangeEnabled = signal(true)

  protected readonly filterStringModel = model('')

  protected readonly filteredEntities = computed((): Entity[] => {
    const filterString = this.filterStringModel().trim().toLowerCase()
    if (filterString) {
      return this.entities().filter(e => accept(e, filterString))
    } else {
      return this.entities() as Entity[]
    }
  })

  protected readonly statusMessage = computed((): string => {
    const entityCount = this.entities().length
    if (entityCount > 0) {
      const status = `${entityCount} item${entityCount === 1 ? '' : 's'}`
      const filteredEntityCount = this.filteredEntities().length
      if (filteredEntityCount < entityCount) {
        return `${filteredEntityCount} of ${status}`
      } else {
        return status
      }
    } else {
      return ''
    }
  })

  protected readonly defaultColDef: ColDef = {
    width: 140,
  }

  protected readonly columnDefs = computed((): ColDef[] => {
    const metadata = this.entityTypeMetadata()
    const typeCode = metadata?.entity_type_code ?? 'CPN'
    const metadataColumns: string[] = metadata ? [...metadata.required, ...metadata.optional] : []
    return [
      ...entityColumnDefs,
      ...metadata?.editable ? effectiveDateColumnDefs : [],
      ...entitySpecificColumnDefs.filter(_ => metadataColumns.includes(_.field!)),
    ].map(colDef => {
      if (colDef.headerName) {
        return colDef
      } else {
        return {
          ...colDef,
          headerName: getDetailsFieldLabel(colDef.field!, typeCode),
        }
      }
    })
  })

  protected readonly getRowId: GetRowIdFunc<Entity> = ({ data }) => data.entity_uid

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedEntity']) {
      window.setTimeout(() => this.syncSelection(), 0)
    }
  }

  private syncSelection() {
    if (this.gridApi) {
      const selectedEntity = this.selectedEntity()
      if (selectedEntity) {
        const node = this.gridApi.getRowNode(selectedEntity.entity_uid)
        if (node && !node.isSelected()) {
          this.selectionChangeEnabled.set(false)
          // when a node is already selected this will cause two events to be posted
          // one to clear the selection and one to select the new thing.
          // ignore selection change events until that is complete
          node.setSelected(true, true)
          this.gridApi.ensureNodeVisible(node)
          window.setTimeout(() => {
            this.selectionChangeEnabled.set(true)
          }, 0)
        }
      } else if (this.gridApi.getSelectedRows().length > 0) {
        this.gridApi.deselectAll()
      }
    }
  }

  protected onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api
    this.syncSelection()
  }

  protected onSelectionChanged({ selectedNodes }: SelectionChangedEvent<Entity>) {
    if (this.selectionChangeEnabled()) {
      const entity = selectedNodes?.[0]?.data
      if (entity !== this.selectedEntity()) {
        this.selectionChange.emit(entity?.entity_uid)
      }
    }
  }
}
